import { ExamEventStatus, useGetMyProctorExamEventsQuery } from '@exam-center/graphql';
import { useNavigate, useParams } from '@exam-center/router';
import { Flex, Loading, Text } from '@shared/components';
import { dayjs, groupBy } from '@shared/utils';
import React, { useMemo } from 'react';

import {
  ExamEventDailySchedule,
  ExamEventScheduleEmptyState,
  type ExamEventScheduleEmptyStateVariant,
} from '../../components';
import { useExamEventScheduleSearchParams } from '../../hooks';

import { mapExamEventsToExamEventDailyScheduleProps, sortExamEvents } from './utils';

const STATUS_LABEL = {
  SCHEDULED: 'กำหนดการคุมสอบ',
  READY_TO_EXAM: 'เตรียมพร้อมสอบ',
  IN_PROGRESS: 'กำลังสอบ',
  COMPLETED: 'สอบเสร็จสิ้น',
  CANCELED: 'ยกเลิกรอบสอบ',
  ALL: 'ทั้งหมด',
};

const STATUS_EMPTY_STATE_VARIANT = {
  SCHEDULED: 'scheduled' as ExamEventScheduleEmptyStateVariant,
  READY_TO_EXAM: 'ready-to-exam' as ExamEventScheduleEmptyStateVariant,
  IN_PROGRESS: 'in-progress' as ExamEventScheduleEmptyStateVariant,
  COMPLETED: 'completed' as ExamEventScheduleEmptyStateVariant,
  CANCELED: 'canceled' as ExamEventScheduleEmptyStateVariant,
};

export default function ProctoringExamEventSchedule() {
  const { examCenterCode } = useParams('/:examCenterCode');

  const navigate = useNavigate();

  const [{ endedAt, registrationStatus, registrationType, startedAt, status, province }] =
    useExamEventScheduleSearchParams();

  const { data, loading: isLoading } = useGetMyProctorExamEventsQuery({
    variables: {
      getMyProctorExamEventsInput: {
        filter: {
          ...(status !== 'ALL' && { status }),
          examCenterCode: examCenterCode,
          provinceIds: !province.length ? null : province,
          registrationStatuses: !registrationStatus.length ? null : registrationStatus,
          registrationTypes: !registrationType.length ? null : registrationType,
          startDate: startedAt,
          endDate: endedAt,
        },
      },
    },
    fetchPolicy: 'network-only',
  });

  const examEvents = useMemo(
    () => sortExamEvents(data?.ecGetMyProctorExamEvents.examEvents ?? []),
    [data],
  );
  const groupedEventsByStatus = groupBy(examEvents, (event) => event.status);

  const allStatuses =
    status === 'ALL'
      ? [
          ExamEventStatus.Scheduled,
          ExamEventStatus.ReadyToExam,
          ExamEventStatus.InProgress,
          ExamEventStatus.Completed,
          ExamEventStatus.Canceled,
        ]
      : [status as ExamEventStatus];

  const groupedEventsByStatusMonthAndDate = allStatuses.map((status) => {
    const events = groupedEventsByStatus[status] || [];

    const eventsGroupedByMonth = groupBy(events, (event) =>
      dayjs(event.examStartedAt).format('เดือนMMMM/YYYY'),
    );

    const monthGroups = Object.entries(eventsGroupedByMonth)
      .sort(([a], [b]) => {
        const format = 'เดือนMMMM/YYYY';
        return dayjs(a, format, 'th').isBefore(dayjs(b, format, 'th')) ? -1 : 1;
      })
      .map(([month, monthEvents]) => {
        const eventsGroupedByDate = groupBy(monthEvents, (event) =>
          dayjs(event.examStartedAt).format('YYYY-MM-DD'),
        );

        const dateGroups = Object.entries(eventsGroupedByDate)
          .sort(([a], [b]) => (dayjs(a).isBefore(dayjs(b)) ? -1 : 1))
          .map(([date, dateEvents]) => ({
            date: date,
            events: dateEvents,
          }));

        return {
          month: month,
          dateGroups: dateGroups,
        };
      });

    return {
      status: status,
      monthGroups: monthGroups,
      eventLength: events.length,
    };
  });

  return (
    <Flex sx={(theme) => ({ flexDirection: 'column', gap: theme.spacing(6) })}>
      <Flex sx={(theme) => ({ flexDirection: 'column', gap: theme.spacing(4) })}>
        {!isLoading && status !== 'ALL' ? (
          <Text variant='h3'>
            {STATUS_LABEL[status]} ({examEvents.length})
          </Text>
        ) : null}
        {isLoading ? (
          <Loading />
        ) : (
          <Flex sx={(theme) => ({ flexDirection: 'column', gap: theme.spacing(4) })}>
            {groupedEventsByStatusMonthAndDate.map((statusGroup) => {
              return (
                <Flex
                  key={statusGroup.status}
                  sx={(theme) => ({ flexDirection: 'column', gap: theme.spacing(4) })}
                >
                  {status === 'ALL' ? (
                    <Text variant='h3'>
                      {STATUS_LABEL[statusGroup.status as ExamEventStatus]} (
                      {statusGroup.eventLength})
                    </Text>
                  ) : null}
                  {statusGroup.monthGroups.length > 0 ? (
                    statusGroup.monthGroups.map((monthGroup) => (
                      <React.Fragment key={monthGroup.month}>
                        <Text variant='h4'>{monthGroup.month}</Text>
                        {monthGroup.dateGroups.map((dateGroup) => {
                          const examEventsPerDate = mapExamEventsToExamEventDailyScheduleProps(
                            dateGroup.events,
                            navigate,
                          );

                          return (
                            <ExamEventDailySchedule
                              key={dateGroup.date}
                              date={dayjs(dateGroup.date).toDate()}
                              examEvents={examEventsPerDate}
                            />
                          );
                        })}
                      </React.Fragment>
                    ))
                  ) : (
                    <ExamEventScheduleEmptyState
                      variant={STATUS_EMPTY_STATE_VARIANT[statusGroup.status]}
                    />
                  )}
                </Flex>
              );
            })}
          </Flex>
        )}
      </Flex>
    </Flex>
  );
}
